<script setup lang="ts">
import debounce from "lodash.debounce";
import { useWordProcessorStore } from "~/components/Base/WordProcessor/useWordProcessorStore";
import type { TeacherCommentCorrection } from "~/composables/useTeacherCommentCorrections";

const emit = defineEmits<{ continue: [] }>();
const props = defineProps<{ isContinuing?: boolean }>();
const documentId = inject("documentId") as string;
const wordProcessorStore = useWordProcessorStore(documentId);
const { submittedDocument } = storeToRefs(useSubmittedDocumentStore(documentId));
const corrections = useDocumentTeacherCommentCorrections(documentId);
const correctionColor = (correction: TeacherCommentCorrection) =>
  wordProcessorStore.getQuillInstance()?.getFormat?.(correction.start, 1)?.color;
const displayedCorrections = shallowRef<(TeacherCommentCorrection & { color?: string })[]>([]);
const syncDisplayedCorrections = () => {
  displayedCorrections.value = corrections.value.map(correction => ({
    ...correction,
    color: correctionColor(correction),
  }));
};
syncDisplayedCorrections();
const refreshList = debounce(syncDisplayedCorrections, 400);
const teacherInkColor = ref<string>();

// Keep the instruction in the teacher's ink even after the last comment is removed.
watch(displayedCorrections, (items) => {
  const color = items.map(item => item.color).find(color => typeof color === "string");
  if (color) teacherInkColor.value = color;
}, { immediate: true });
const instructionColor = computed(() => teacherInkColor.value ??
  useArrayToRGB(submittedDocument.value?.inkColor ?? []));

const hasAttemptedContinue = ref(false);
const showRemainingComments = computed(() => hasAttemptedContinue.value && displayedCorrections.value.length > 0);
const hoveredCorrectionId = ref<string>();
const focusedCorrectionId = ref<string>();
const highlightedCorrection = computed(() => {
  if (!showRemainingComments.value) return undefined;
  const displayed = displayedCorrections.value.find(
    correction => correction.id === (hoveredCorrectionId.value ?? focusedCorrectionId.value)
  );
  return displayed && corrections.value.find(correction =>
    correction.id === displayed.id && correction.text === displayed.text && correction.kind === displayed.kind
  );
});

watch(corrections, () => {
  // Old positions must not highlight unrelated text while the list catches up.
  hoveredCorrectionId.value = undefined;
  focusedCorrectionId.value = undefined;
  refreshList();
});
onUnmounted(() => refreshList.cancel());

const markAsComplete = () => {
  if (props.isContinuing) return;
  // Validation and its feedback always use the latest editor contents.
  refreshList.cancel();
  syncDisplayedCorrections();
  hasAttemptedContinue.value = true;
  if (corrections.value.length === 0) emit("continue");
};
</script>

<template>
  <div data-step-id="guided-corrections" class="flex flex-col gap-2">
    <DocumentSelfAssessmentCorrectionHighlight :range="highlightedCorrection" />
    <div class="border-b px-4 pb-2 pt-4">
      <h2 class="text-h5 font-bold">Make Corrections</h2>
    </div>

    <div v-auto-animate class="px-4">
      <BaseLabelBadge v-if="showRemainingComments" color="danger" role="alert" class="mb-2">
        There are still teacher comments for you to review
      </BaseLabelBadge>
      <BaseLabelBadge class="mb-2">
        Read <span :style="{ color: instructionColor }">teacher comments</span> and make the necessary corrections.
      </BaseLabelBadge>

      <section v-if="showRemainingComments" class="mb-3">
        <h3 class="mb-2 text-body-2 font-bold">Remaining teacher comments</h3>
        <ul class="list-disc space-y-2 pl-5">
          <li
            v-for="correction in displayedCorrections"
            :key="correction.id"
            tabindex="0"
            class="rounded-sm pl-1 text-base leading-relaxed focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-default"
            @mouseenter="hoveredCorrectionId = correction.id"
            @mouseleave="hoveredCorrectionId = undefined"
            @focus="focusedCorrectionId = correction.id"
            @blur="focusedCorrectionId = undefined"
          >
            <span
              class="whitespace-pre-wrap"
              :style="{ color: correction.color ?? instructionColor }"
              :class="{ 'line-through': correction.kind === 'deletion' }"
            >{{ correction.text.trim() }}</span>
          </li>
        </ul>
      </section>

      <div class="flex justify-end">
        <BaseButton
          type="button"
          :disabled="props.isContinuing"
          :show-spinner="props.isContinuing"
          @click="markAsComplete"
        >
          I have done this
        </BaseButton>
      </div>
    </div>
  </div>
</template>
